import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useResizeDetector } from 'react-resize-detector'

import { Allotment } from 'allotment'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { skipToken } from '@reduxjs/toolkit/query'

import { ElaraErrorBoundary } from '@/components/elara/ElaraErrorBoundary'
import { useCreateUploadGrantMutation } from '@/core/services/file'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { BillingReviewStatus, ConsultationReadinessStatus, ConsultationStatus } from '@/core/models/consultation.model'
import { useGetConsultationQuery, useSetConsultationReadinessMutation } from '@/core/services/consultation'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { useBillingDialogEnabled } from '@/hooks/useBillingDialogEnabled'
import { getApiErrorMessage } from '@/utils/errors'

import { useConsultationRTCConfig } from '@/features/diagnostic'

import { useIntro } from '@/hooks/useIntro'
import useMedconnectDevices from '@/hooks/useMedconnectDevices'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import ConsultationPaymentDialog, { useConsultationPaymentDialog } from '@/pages/consultation/components/ConsultationPaymentDialog'
import QueryBoundary from '@/components/ui/template/QueryBoundary'
import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { confirmReadingMessages } from '@/redux/reducers/chat-room.reducer'

import { DiagnoticLiveIntro } from '@/utils/onboarding/intro'
import { ElaraRealtimeProvider } from '@/providers/elara/ElaraRealtimeProvider'
import { useElaraRealtimeLicenseEnabled } from '@/providers/elara/hooks/useElaraRealtimeLicenseEnabled'
import { isStaff } from '@/utils/type-guards'

import { useConsultationState } from '../model/useConsultationState'
import { useReadinessChecklist } from '../model/useReadinessChecklist'
import { ConfirmReadyDialog } from './ConfirmReadyDialog'
import { useSidebarMenu } from '../model/useSidebarMenu'
import { usePageSetup } from '../model/usePageSetup'
import { useConsultationLifecycle } from '../model/useConsultationLifecycle'
import { useJoinMeeting } from '../model/useJoinMeeting'
import { usePatientConsentDialog } from '../model/usePatientConsentDialog'
import { useParticipants } from '../model/useParticipants'

import EndLiveConsultationDialog from '@/pages/diagnostic/containers/EndLiveConsultationDialog'
import InvitationDialog from '@/pages/diagnostic/containers/InvitationDialog'
import Messenger from '@/pages/diagnostic/containers/Messenger'
import MobileConsultation from '@/pages/diagnostic/containers/MobileConsultation'
import MobileConsultationTopbar from '@/pages/diagnostic/containers/MobileConsultationTopbar'
import { Visio } from '@/pages/diagnostic/containers/Visio'
import PatientConsentDialog from '@/pages/diagnostic/components/PatientConsentDialog'
import { ProfessionalConsentDialog } from '@/pages/diagnostic/components/professional-consent'
import QrCodeDialog from '@/pages/diagnostic/components/QrCodeDialog'

import { Content } from './Content'
import { ElaraNavigationActions } from './ElaraNavigationActions'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { useTheme } from '@mui/material/styles'
import { useIsSimplifiedReceptionLocked } from '@/hooks/useIsSimplifiedReceptionLocked'
import { ReceptionConsultationSteps } from '@/pages/simplified-reception/ui/consultation'

type ConsultationLivePageProps = {}

export function ConsultationLivePage(props: ConsultationLivePageProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { consultationId } = useParams()
  const [searchParams] = useSearchParams()
  const { width: splitterWrapperWidth = 0, ref: splitterWrapperRef } = useResizeDetector()
  const isTooSmall = useIsMobile()

  const { data: consultation, isLoading, isError, error } = useGetConsultationQuery(consultationId ?? skipToken)

  // State hooks
  const { room, participants, unreadChatMessages } = useConsultationState(consultationId)
  const { menuItems, selectedMenuItem, setSelectedMenuItem } = useSidebarMenu(consultationId)
  const { handleJoinMeeting, isStreamer } = useJoinMeeting({ consultationId, room })
  const { doctor, pds, isDoctor, isDoctorOnline, isPdsOnline } = useParticipants({ consultation, participants })
  // Simplified reception: a guided identity → vital signs → doctor flow replaces the menu and its sections.
  const isReceptionLocked = useIsSimplifiedReceptionLocked()
  const theme = useTheme()
  const consentDialog = usePatientConsentDialog({ consultation })
  const hasElaraRealtimeLicense = useElaraRealtimeLicenseEnabled()

  // Billing review / payment dialog
  const { open: paymentDialogOpen, consultationId: paymentDialogConsultationId, showDialog, autoShowDialog, hideDialog } = useConsultationPaymentDialog()
  const waitingRoomId = consultation?.waitingRoom // string ID or undefined
  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)

  // Local state
  const [isInfo, setIsInfo] = useState<boolean>(isTooSmall ? true : false)
  const [isSidebarVisible, setSidebarVisible] = useState<boolean>(true)
  const [diagnosticSubTab, setDiagnosticSubTab] = useState<string | null>(null)
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState<boolean>(false)
  const [elaraQrCodeOpen, setElaraQrCodeOpen] = useState<boolean>(false)
  const [uploadGrantUrl, setUploadGrantUrl] = useState<string>('')
  const [createUploadGrant] = useCreateUploadGrantMutation()

  const showElaraQrCode = useCallback(() => {
    if (!consultationId) return
    createUploadGrant({ objectType: 'consultation', objectId: consultationId })
      .unwrap()
      .then(({ url }) => {
        setUploadGrantUrl(url)
        setElaraQrCodeOpen(true)
      })
      .catch(() => undefined)
  }, [consultationId, createUploadGrant])

  // Mobile topbar dialogs (only for mobile view)
  const [mobileMessengerOpen, setMobileMessengerOpen] = useState<boolean>(false)
  const [mobileParticipantsDialogOpen, setMobileParticipantsDialogOpen] = useState<boolean>(false)
  const [mobileEndConsultationDialogOpen, setMobileEndConsultationDialogOpen] = useState<boolean>(false)

  const authUser = useAppSelector(selectAuthUser)

  // RTC configuration
  const {
    customDevices,
    medicalVideoAttendeeInfo,
    medicalVideoMeetingInfo,
    peerjsConfig,
    rpiConfig,
    rpiMeetingId,
    stethoscopeAttendeeInfo,
    stethoscopeMeetingInfo,
  } = useConsultationRTCConfig({ streamer: room?.streamer })

  const isConsultationActive = consultation?.status === 'active'
  const isNetsoinsConsultation = false // TODO: add
  const readOnly = false

  const onSizeChange = useCallback(
    (size: number[]) => {
      if (size[0] > splitterWrapperWidth / 5) setSidebarVisible(true)
      else setSidebarVisible(false)
    },
    [splitterWrapperWidth]
  )

  // Lifecycle hooks
  usePageSetup()
  useConsultationLifecycle({
    consultation,
    consultationId,
    room,
    isStreamer,
    paymentMenuSearchParam: searchParams.get('menu_item'),
  })
  useMedconnectDevices(isStaff(authUser) ? authUser.status === 'PDS' : false, consultationId ?? '')
  useIntro(DiagnoticLiveIntro, !(!authUser || !consultation || !room))

  // Billing review logic
  const billingReviewPending = !consultation?.billingReview || consultation.billingReview.status === BillingReviewStatus.Pending
  const billingDialogEnabledForUser = useBillingDialogEnabled(waitingRoom)

  // Operator (PDS) "mark ready for doctor" — opt-in per room, shown only while the patient is still
  // waiting (Scheduled) for a doctor to pick them up.
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const readinessEnabled = waitingRoom?.readinessConfig?.enabled ?? false
  const isConsultationReady = consultation?.readiness?.status === ConsultationReadinessStatus.Ready
  const [setConsultationReadiness, { isLoading: isTogglingReadiness }] = useSetConsultationReadinessMutation()
  const checklist = useReadinessChecklist({ consultation, waitingRoom })
  const [confirmReadyOpen, setConfirmReadyOpen] = useState(false)
  const doToggleReadiness = async () => {
    if (!consultationId) return
    try {
      await setConsultationReadiness({ id: consultationId, changes: { ready: !isConsultationReady } }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.readiness.error')), { variant: 'error' })
    }
  }
  const handleToggleReadiness = async () => {
    // Soft confirm: marking ready before the preparation is complete asks first, never blocks.
    // "Complete" = all boxes ticked (boolean template) or the form submitted (any field type).
    if (!isConsultationReady && checklist.enabled && !checklist.isComplete) {
      setConfirmReadyOpen(true)
      return
    }
    await doToggleReadiness()
  }

  useEffect(() => {
    if (!consultation || !consultationId) return
    if (consultation.status !== ConsultationStatus.Completed) return
    if (!billingDialogEnabledForUser) return
    if (!billingReviewPending) return

    autoShowDialog(consultationId)
  }, [consultation?.status, billingDialogEnabledForUser, billingReviewPending, consultationId, autoShowDialog])

  // Browser-level guard: warn the user if they try to close the tab / navigate
  // away while billing review is still pending on a completed consultation.
  useEffect(() => {
    const shouldWarn = consultation?.status === ConsultationStatus.Completed && billingDialogEnabledForUser && billingReviewPending
    if (!shouldWarn) return

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [consultation?.status, billingDialogEnabledForUser, billingReviewPending])

  // Mark messages as read when mobile messenger is open (for mobile view only)
  useEffect(() => {
    if (isTooSmall && consultation?.chatRoom && mobileMessengerOpen && unreadChatMessages.length) {
      dispatch(confirmReadingMessages(consultation.chatRoom))
    }
  }, [isTooSmall, consultation?.chatRoom, dispatch, mobileMessengerOpen, unreadChatMessages.length])

  // Stable callbacks for ELARA actions
  const handleSelectMenuItem = useCallback(
    (id: string) => {
      setSelectedMenuItem(id)
      setDiagnosticSubTab(null)
    },
    [setSelectedMenuItem]
  )

  // Mobile handlers
  const toggleMobileMessenger = () => setMobileMessengerOpen((prev) => !prev)

  const handleMobileCloseConsultation = () => {
    if (isNetsoinsConsultation) {
      navigate('/netsoins/0')
    } else {
      setMobileMessengerOpen(false)
      setMobileEndConsultationDialogOpen(true)
    }
  }

  if (!authUser) return null

  if (!consultation || !room) {
    return <QueryBoundary isLoading={isLoading} isError={isError} error={error} fallback={null} />
  }

  if (consultation.type === 'local') return <NotFoundPage />

  const elaraWrapper = hasElaraRealtimeLicense
    ? (children: React.ReactNode) => <ElaraRealtimeProvider consultationId={consultationId!}>{children}</ElaraRealtimeProvider>
    : (children: React.ReactNode) => <>{children}</>

  const contentPane = (
    <Allotment.Pane key="content" minSize={0.5}>
      {isReceptionLocked ? (
        <ReceptionConsultationSteps
          consultation={consultation}
          consultationId={consultationId}
          doctorName={doctor || undefined}
          isDoctorOnline={isDoctorOnline}
          isStreamer={isStreamer}
          rpiMeetingId={rpiMeetingId ?? undefined}
        />
      ) : (
        <Content
          consultation={consultation}
          consultationId={consultationId}
          selectedMenuItem={selectedMenuItem}
          isSidebarVisible={isSidebarVisible}
          customDevices={customDevices}
          isStreamer={isStreamer}
          medicalVideoAttendeeInfo={medicalVideoAttendeeInfo}
          medicalVideoMeetingInfo={medicalVideoMeetingInfo}
          stethoscopeAttendeeInfo={stethoscopeAttendeeInfo}
          stethoscopeMeetingInfo={stethoscopeMeetingInfo}
          peerjsConfig={peerjsConfig}
          rpiConfig={rpiConfig}
          rpiMeetingId={rpiMeetingId ?? undefined}
          room={room}
          diagnosticSubTab={diagnosticSubTab}
          onDiagnosticSubTabChange={() => setDiagnosticSubTab(null)}
        />
      )}
    </Allotment.Pane>
  )
  const visioPane = (
    <Allotment.Pane key="visio" minSize={0.5}>
      <Visio consultation={consultation} handleJoinMeeting={handleJoinMeeting} />
    </Allotment.Pane>
  )
  const mirrorPanes = isReceptionLocked && theme.direction === 'rtl'

  return elaraWrapper(
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      {isTooSmall ? (
        <MobileConsultationTopbar
          authUser={authUser}
          consultation={consultation}
          pds={pds}
          doctor={doctor}
          isPdsOnline={isPdsOnline}
          isDoctorOnline={isDoctorOnline}
          readOnly={readOnly}
          consultationId={consultationId}
          openParticipantsDialog={mobileParticipantsDialogOpen}
          setOpenParticipantsDialog={setMobileParticipantsDialogOpen}
          unreadChatMessages={unreadChatMessages}
          toggleMessenger={toggleMobileMessenger}
          handleCloseConsultation={handleMobileCloseConsultation}
          isConsultationActive={isConsultationActive}
          isInfo={isInfo}
          setIsInfo={setIsInfo}
        />
      ) : (
        <Topbar
          authUser={authUser}
          consultation={consultation}
          consultationId={consultationId}
          doctor={doctor}
          isConsultationActive={isConsultationActive}
          isDoctorOnline={isDoctorOnline}
          isDoctor={isDoctor}
          isPdsOnline={isPdsOnline}
          pds={pds}
          readOnly={readOnly}
          unreadChatMessages={unreadChatMessages}
          billingReviewPending={billingDialogEnabledForUser && billingReviewPending}
          billingDialogEnabledForUser={billingDialogEnabledForUser}
          onShowBillingDialog={() => consultationId && showDialog(consultationId)}
          readinessEnabled={readinessEnabled}
          isConsultationReady={isConsultationReady}
          isTogglingReadiness={isTogglingReadiness}
          onToggleReadiness={handleToggleReadiness}
          checklist={checklist}
        />
      )}

      <ConfirmReadyDialog
        open={confirmReadyOpen}
        uncheckedCount={checklist.uncheckedCount}
        onConfirm={doToggleReadiness}
        onClose={() => setConfirmReadyOpen(false)}
      />

      {isTooSmall && isInfo && (
        <MobileConsultation
          consultationId={consultationId}
          menuItems={menuItems.filter((item) => item.isMenuDiagnostic)}
          stethoscopeAttendeeInfo={stethoscopeAttendeeInfo}
          stethoscopeMeetingInfo={stethoscopeMeetingInfo}
          audioPeerjsConfig={peerjsConfig}
          videoPeerjsConfig={peerjsConfig}
          rpiConfig={rpiConfig}
          customDevices={customDevices}
          medicalVideoAttendeeInfo={medicalVideoAttendeeInfo}
          medicalVideoMeetingInfo={medicalVideoMeetingInfo}
        />
      )}

      {isTooSmall && !isInfo && <Visio consultation={consultation} handleJoinMeeting={handleJoinMeeting} />}

      {!isTooSmall && (
        <>
          {!isReceptionLocked && (
            <Sidebar
              consultationId={consultationId}
              menuItems={menuItems.filter((item) => !item.isMenuDiagnostic)}
              onSelect={handleSelectMenuItem}
              selectedMenuItem={selectedMenuItem}
              documentsDialogOpen={documentsDialogOpen}
              onDocumentsDialogChange={setDocumentsDialogOpen}
            />
          )}
          <Box data-testid="splitter-wrapper" ref={splitterWrapperRef} sx={{ flex: 1, height: '100%' }}>
            {/* Right-to-left reception: the guided steps sit on the right, the call on the left. */}
            <Allotment defaultSizes={mirrorPanes ? [40, 60] : [60, 40]} onChange={onSizeChange}>
              {mirrorPanes ? [visioPane, contentPane] : [contentPane, visioPane]}
            </Allotment>
          </Box>
        </>
      )}

      {/* Mobile-specific dialogs (only shown on mobile) */}
      {isTooSmall && (
        <>
          {!readOnly && (
            <InvitationDialog open={mobileParticipantsDialogOpen} consultation={consultation} onClose={() => setMobileParticipantsDialogOpen(false)} />
          )}
          <EndLiveConsultationDialog
            onClose={() => setMobileEndConsultationDialogOpen(false)}
            open={mobileEndConsultationDialogOpen}
            billingReviewPending={billingDialogEnabledForUser && billingReviewPending && consultation.status === ConsultationStatus.Completed}
            onReviewBilling={() => {
              setMobileEndConsultationDialogOpen(false)
              if (consultationId) showDialog(consultationId)
            }}
          />
          <Messenger chatRoomId={consultation.chatRoom} onClose={toggleMobileMessenger} open={mobileMessengerOpen} />
        </>
      )}

      <ConsultationPaymentDialog open={paymentDialogOpen} consultationId={paymentDialogConsultationId} onClose={hideDialog} consultation={consultation} />

      {/* Patient consent dialog - shown for PDS users */}
      {consentDialog.shouldShowConsentDialog && (
        <PatientConsentDialog
          open={consentDialog.isConsentDialogOpen}
          onClose={consentDialog.handleConsentResponse}
          consultationId={consultationId}
          professionalConsent={consultation?.professionalConsent}
          showTreatingPhysician={consentDialog.showTreatingPhysician}
        />
      )}
      <ProfessionalConsentDialog consultationId={consultationId} professionalConsent={consultation?.professionalConsent} />
      <QrCodeDialog open={elaraQrCodeOpen} onClose={() => setElaraQrCodeOpen(false)} qrCodeValue={uploadGrantUrl} />
      {hasElaraRealtimeLicense && (
        <ElaraErrorBoundary>
          <ElaraNavigationActions
            selectedMenuItem={selectedMenuItem}
            diagnosticSubTab={diagnosticSubTab}
            onSelectMenuItem={handleSelectMenuItem}
            onSetDiagnosticSubTab={setDiagnosticSubTab}
            onOpenDocuments={() => setDocumentsDialogOpen(true)}
            onCloseDocuments={() => setDocumentsDialogOpen(false)}
            onShowQrCode={showElaraQrCode}
            consultationId={consultationId!}
          />
        </ElaraErrorBoundary>
      )}
    </Stack>
  )
}
